import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authAPI, organizationAPI } from '../services/api';

const OrgContext = createContext();

export function OrgProvider({ children }) {
  const [currentOrgId, setCurrentOrgId] = useState(() => {
    const saved = localStorage.getItem('selectedOrgId');
    if (!saved || saved === 'null') return null;

    const parsed = Number.parseInt(saved, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }); // null = Personal context
  const [organizations, setOrganizations] = useState([]);
  const [currentOrgRole, setCurrentOrgRole] = useState(null);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const orgLoadAttempted = useRef(false);

  // Load current user + organizations eagerly on mount
  useEffect(() => {
    let cancelled = false;

    const loadBootstrapData = async () => {
      // Load user info
      try {
        const response = await authAPI.getMe({ skipAuthRedirect: true });
        if (!cancelled) {
          setIsSystemAdmin(Boolean(response.data?.is_admin));
        }
      } catch (err) {
        if (!cancelled) {
          setIsSystemAdmin(false);
          setOrganizations([]);
          setOrgsLoaded(false);
          orgLoadAttempted.current = false;
        }
        return;
      }

      // Load organizations eagerly so enabled_features are available
      // before OrgSwitcher mounts (sidebar may be collapsed)
      if (!orgLoadAttempted.current) {
        orgLoadAttempted.current = true;
        try {
          const orgResponse = await organizationAPI.listOrganizations();
          if (!cancelled) {
            const orgs = Array.isArray(orgResponse.data) ? orgResponse.data : [];
            setOrganizations(orgs);
            setOrgsLoaded(true);
          }
        } catch (err) {
          if (!cancelled) {
            setOrganizations([]);
            setOrgsLoaded(true);
          }
        }
      }
    };

    loadBootstrapData();

    return () => {
      cancelled = true;
    };
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    if (currentOrgId === null) {
      localStorage.setItem('selectedOrgId', 'null');
    } else {
      localStorage.setItem('selectedOrgId', currentOrgId.toString());
    }
  }, [currentOrgId]);

  // Update role when org changes
  useEffect(() => {
    if (currentOrgId === null) {
      setCurrentOrgRole(null);
    } else {
      const org = organizations.find(o => o.id === currentOrgId);
      setCurrentOrgRole(org?.user_role || null);
    }
  }, [currentOrgId, organizations]);

  // Auto-select first org if user is in an invalid or null context
  useEffect(() => {
    if (organizations.length > 0) {
      const selectableOrgs = organizations.filter(o => o.is_operational_workspace !== false && o.account_type !== 'parent');
      const isValidOrg = selectableOrgs.some(o => o.id === currentOrgId);
      if (!isValidOrg) {
        setCurrentOrgId(selectableOrgs[0]?.id || null);
      }
    }
  }, [currentOrgId, organizations]);

  const switchToOrg = useCallback((orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (org?.is_operational_workspace === false || org?.account_type === 'parent') {
      return;
    }
    setCurrentOrgId(orgId);
  }, [organizations]);

  const switchToPersonal = useCallback(() => {
    setCurrentOrgId(null);
  }, []);

  // Allow OrgSwitcher (or other components) to refresh the org list
  const refreshOrganizations = useCallback(async () => {
    setOrgsLoaded(false);
    orgLoadAttempted.current = true;

    try {
      const response = await organizationAPI.listOrganizations();
      const orgs = Array.isArray(response.data) ? response.data : [];
      setOrganizations(orgs);
      setOrgsLoaded(true);
      return orgs;
    } catch (err) {
      console.error('Failed to refresh organizations:', err);
      setOrganizations([]);
      setOrgsLoaded(false);
      orgLoadAttempted.current = false;
      return [];
    }
  }, []);

  const getCurrentOrg = useCallback(() => {
    if (currentOrgId === null) return null;
    return organizations.find(o => o.id === currentOrgId);
  }, [currentOrgId, organizations]);

  const isPersonalContext = useCallback(() => currentOrgId === null, [currentOrgId]);

  const isOrgAdmin = useCallback(() => {
    return isSystemAdmin || currentOrgRole === 'owner' || currentOrgRole === 'admin' || currentOrgRole === 'system_admin';
  }, [currentOrgRole, isSystemAdmin]);

  const canSeeAllProjects = useCallback(() => {
    return isSystemAdmin || currentOrgRole === 'owner' || currentOrgRole === 'admin' || currentOrgRole === 'system_admin';
  }, [currentOrgRole, isSystemAdmin]);

  const value = useMemo(() => ({
    currentOrgId,
    currentOrgRole,
    isSystemAdmin,
    organizations,
    setOrganizations,
    orgsLoaded,
    refreshOrganizations,
    switchToOrg,
    switchToPersonal,
    getCurrentOrg,
    isPersonalContext,
    isOrgAdmin,
    canSeeAllProjects
  }), [
    currentOrgId,
    currentOrgRole,
    isSystemAdmin,
    organizations,
    orgsLoaded,
    refreshOrganizations,
    switchToOrg,
    switchToPersonal,
    getCurrentOrg,
    isPersonalContext,
    isOrgAdmin,
    canSeeAllProjects
  ]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within OrgProvider');
  }
  return context;
}

export default OrgContext;

